import { useRef, useState } from "react";
import { Paperclip, X, UploadCloud, AlertCircle } from "lucide-react";

export const ATTACH_EXT = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx", "txt", "csv"];
export const MAX_FILES = 10;
export const MAX_FILE = 10 * 1024 * 1024;
export const MAX_TOTAL = 20 * 1024 * 1024;

export const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${b === 0 ? 0 : Math.max(1, Math.round(b / 1024))} KB`);
const extOf = (n) => (n.includes(".") ? n.split(".").pop().toLowerCase() : "");

export function validateAttachments(current, incoming) {
  const errors = [];
  const next = [...current];
  for (const f of incoming) {
    const ext = extOf(f.name);
    if (!ATTACH_EXT.includes(ext)) { errors.push(`${f.name}: unsupported file type`); continue; }
    if (f.size === 0) { errors.push(`${f.name}: file is empty`); continue; }
    if (f.size > MAX_FILE) { errors.push(`${f.name}: larger than 10 MB`); continue; }
    if (next.some((x) => x.name === f.name && x.size === f.size)) { errors.push(`${f.name}: already attached`); continue; }
    if (next.length >= MAX_FILES) { errors.push(`${f.name}: maximum ${MAX_FILES} files per email`); continue; }
    if (next.reduce((s, x) => s + x.size, 0) + f.size > MAX_TOTAL) { errors.push(`${f.name}: would exceed the 20 MB combined limit`); continue; }
    next.push(f);
  }
  return { next, errors };
}

export function EmailAttachments({ files, onChange, disabled }) {
  const inputRef = useRef(null);
  const [errors, setErrors] = useState([]);
  const [drag, setDrag] = useState(false);
  const total = files.reduce((s, f) => s + f.size, 0);

  const add = (list) => {
    const { next, errors: errs } = validateAttachments(files, Array.from(list || []));
    setErrors(errs);
    onChange(next);
  };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (!disabled) add(e.dataTransfer.files); };

  return (
    <div className="space-y-2" data-testid="email-attachments">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-300">Attachments</span>
        <span className="text-xs text-neutral-500" data-testid="attachment-count">{files.length} / {MAX_FILES} files · {fmtSize(total)} of 20 MB</span>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-4 text-center transition-colors ${drag ? "border-[#E30613] bg-[#E30613]/10" : "border-white/[0.12] bg-[#0A0A0A]"}`}
        data-testid="attachment-dropzone"
      >
        <UploadCloud className="h-5 w-5 text-neutral-500" />
        <p className="text-xs text-neutral-500">Drag files here, or</p>
        <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.12] px-3 py-1.5 text-sm text-neutral-200 hover:bg-white/[0.05] disabled:opacity-50" data-testid="attach-files-btn">
          <Paperclip className="h-4 w-4" /> Attach Files
        </button>
        <p className="text-[11px] text-neutral-600">PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, TXT, CSV · max 10 MB each, 20 MB total</p>
        <input ref={inputRef} type="file" multiple hidden accept={ATTACH_EXT.map((e) => `.${e}`).join(",")} onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="attach-files-input" />
      </div>
      {errors.length > 0 && (
        <div className="space-y-1 rounded-lg border border-[#E30613]/30 bg-[#E30613]/10 px-3 py-2" data-testid="attachment-errors">
          {errors.map((er) => <p key={er} className="flex items-start gap-1.5 text-xs text-[#FF6069]"><AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />{er}</p>)}
        </div>
      )}
      {files.length > 0 && (
        <ul className="space-y-1.5" data-testid="attachment-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${f.size}`} className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2" data-testid={`attachment-item-${i}`}>
              <span className="rounded bg-[#E30613]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#FF6069]">{extOf(f.name)}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-white" title={f.name}>{f.name}</span>
              <span className="text-xs text-neutral-500">{fmtSize(f.size)}</span>
              <button type="button" disabled={disabled} onClick={() => onChange(files.filter((_, j) => j !== i))} className="text-neutral-500 hover:text-[#FF6069] disabled:opacity-50" aria-label={`Remove ${f.name}`} data-testid={`remove-attachment-${i}`}><X className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
